import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import {
  container_type,
  decimal,
} from '@trashlab/api/src/domains/core/schemas/base-template';
import { Decimal } from 'decimal.js';
import { sql } from 'drizzle-orm';
import {
  boolean,
  index,
  pgEnum,
  pgTable,
  text,
  unique,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { business_line_config } from '@trashlab/api/src/domains/business-lines/schemas/business-line';

export const product_type = pgEnum('product_type', [
  'CONTAINER',
  'SERVICE_ONLY',
]);

/**
 * Represents a physical container or equipment product used in waste management services.
 * Products can be containers like dumpsters, liquid waste containers, front load containers, or carts.
 * Each product has specific capacity measurements and can be associated with SKUs for pricing.
 */
export const product = pgTable(
  'product',
  {
    ...defaultFields.fieldsWithTenantId(),
    // This is used as the primary name of the product/container.
    cubicYardage: varchar('cubicYardage').notNull(),
    /** Type of container (DUMPSTER, LIQUID_WASTE_CONTAINER, FRONT_LOAD, CART) */
    containerType: container_type('containerType').notNull(),
    /** Short identifier code for quick reference */
    shortCode: varchar('shortCode', { length: 255 }),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Path to product image used in online ordering */
    onlineOrderingImagePath: varchar('onlineOrderingImagePath', {
      length: 255,
    }),
    /** Detailed description for online shop display */
    shopDescription: text('shopDescription'),
    /** Precise cubic yard capacity as decimal */
    yardage: decimal('yardage', { precision: 5, scale: 2 })
      .default(new Decimal(0))
      .notNull(),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Business line this product belongs to */
    businessLineId: uuid('businessLineId').references(
      () => business_line_config.id,
    ),
    /** Classifies whether orders for this product use a container or are service-only. */
    productType: product_type('productType').default('CONTAINER').notNull(),
    /** Internal staff-app illustration; online ordering requires explicit opt-in. */
    imageKey: varchar('imageKey', { length: 64 }),
    useIllustrationInOnlineOrdering: boolean('useIllustrationInOnlineOrdering')
      .notNull()
      .default(false),
    /** Tenant-managed variant that splits capability matching within one type and size. */
    variantId: uuid('variantId'),
  },
  (table) => {
    return {
      catalog_live_page_idx: index('product_catalog_live_page_idx')
        .on(table.tenantId, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      createdAtTs_idx: index('product_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('product_tenantId_idx').on(table.tenantId),
      tenant_id_key: unique('product_tenant_id_key').on(table.tenantId, table.id),
      updatedAtTs_idx: index('product_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('product_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('product_versionNumber_idx').on(
        table.versionNumber,
      ),
      // Covering index for product lookups by tenant and container type
      tenant_container_type_idx: index('idx_product_tenant_container_type').on(
        table.tenantId,
        table.containerType,
      ),
      materializer_match_idx: index('product_capability_materializer_match_idx')
        .on(table.tenantId, table.containerType, table.yardage)
        .where(sql`${table.productType} = 'CONTAINER' AND ${table.archivedAtTs} IS NULL`),
      businessLineId_idx: index('product_businessLineId_idx').on(
        table.businessLineId,
      ),
      mobile_label_search_idx: index('product_mobile_label_search_idx')
        .using('gin', table.tenantId, table.cubicYardage.op('gin_trgm_ops'))
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_display_label_page_active_idx: index(
        'product_tenant_display_label_page_active_idx',
      )
        .on(
          table.tenantId,
          sql`lower(left(public.asset_product_display_label("cubicYardage", "shortCode", "containerType"), 48) COLLATE "C") COLLATE "C"`,
          sql`left(public.asset_product_display_label("cubicYardage", "shortCode", "containerType"), 48) COLLATE "C"`,
          sql`md5(public.asset_product_display_label("cubicYardage", "shortCode", "containerType")) COLLATE "C"`,
          table.id,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
    };
  },
);

/**
 * Historical record of changes to products.
 * Tracks all modifications to product records over time for auditing and versioning.
 * Contains the same fields as product but allows nulls for partial updates.
 */
export const product_history = pgTable('product_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  cubicYardage: varchar('cubicYardage'),
  containerType: container_type('containerType'),
  shortCode: varchar('shortCode', { length: 255 }),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  onlineOrderingImagePath: varchar('onlineOrderingImagePath', { length: 255 }),
  shopDescription: text('shopDescription'),
  yardage: decimal('yardage', { precision: 5, scale: 2 }),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  businessLineId: uuid('businessLineId'),
  productType: product_type('productType').default('CONTAINER'),
  imageKey: varchar('imageKey', { length: 64 }),
  useIllustrationInOnlineOrdering: boolean('useIllustrationInOnlineOrdering'),
  variantId: uuid('variantId'),
});

/** Type inference for product schema */
export type ProductSchema = typeof product.$inferSelect;
/** Type inference for product insert operations */
export type ProductInsertSchema = typeof product.$inferInsert;
